import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { StreakCelebrationVariant } from 'src/components/Streak/celebration/StreakCelebration';
import { ROUTES } from 'src/config/routes';
import { logAnalyticsEvent } from 'src/modules/analytics/analytics';
import { useStreakSummary } from 'src/queries/streak';
import {
  isStreakGoalActive,
  isStreakGoalReached,
  scopedStorageKey,
  useClaimStreakGoal,
  useCurrentUserId,
  useStreakCelebrationTiers,
  useStreakGoal,
} from 'src/queries/streakGoal';
import { readStoredItem, writeStoredItem } from 'src/utils/storageManager';

export const STREAK_CELEBRATED_STORAGE_KEY = 'streak-celebrated-length';

export const DETOUR_STATE_KEY = 'detourFrom';

const readAcknowledged = (key: string): number =>
  Number(readStoredItem(key) ?? '0');

const writeAcknowledged = (key: string, value: number | null): boolean =>
  writeStoredItem(key, String(value));

const useStreakCelebration = ({
  isArmed,
  detourState,
}: {
  isArmed: boolean;
  detourState?: Record<string, unknown>;
}) => {
  const { data: summary, isError } = useStreakSummary({ isEnabled: isArmed });
  const userId = useCurrentUserId();
  const goal = useStreakGoal({ isEnabled: isArmed });
  const celebrationTiers = useStreakCelebrationTiers({ isEnabled: isArmed });
  const claimGoal = useClaimStreakGoal();
  const navigate = useNavigate();

  const storageKey = scopedStorageKey(STREAK_CELEBRATED_STORAGE_KEY, userId);

  // Re-read the acknowledged length each time the hook arms: another instance may have acknowledged a newer one.
  const [seen, setSeen] = useState<{
    key: string;
    armed: boolean;
    value: number;
  } | null>(null);
  let acknowledged: number;
  if (seen && seen.key === storageKey && (!isArmed || seen.armed)) {
    acknowledged = seen.value;
  } else {
    acknowledged = readAcknowledged(storageKey);
    setSeen({ key: storageKey, armed: isArmed, value: acknowledged });
  }
  if (seen && seen.key === storageKey && seen.armed && !isArmed) {
    setSeen({ ...seen, armed: false });
  }

  const [dismissedAt, setDismissedAt] = useState<number | null>(null);

  const length = isArmed && summary ? summary.current_length : null;
  const isNew = length !== null && length !== acknowledged;
  const isReady = isArmed && (summary !== undefined || isError);

  let variant: StreakCelebrationVariant | null = null;
  if (isNew && length !== null && length !== dismissedAt) {
    if (isStreakGoalActive(goal, length) && isStreakGoalReached(goal, length)) {
      variant = 'goal';
    } else if (length === 1) variant = 'start';
    else if (celebrationTiers.includes(length)) variant = 'milestone';
  }

  useEffect(() => {
    if (!isNew) return;
    writeAcknowledged(storageKey, length);
  }, [isNew, length, storageKey]);

  useEffect(() => {
    if (variant === null || length === null) return;
    void logAnalyticsEvent('streak_celebration_viewed', {
      current_streak: length,
      celebration_variant: variant,
    });
  }, [variant, length]);

  return {
    variant,
    streakLength: length ?? 0,
    goal,
    isReady,
    dismiss: (): boolean => {
      if (variant !== null && length !== null) {
        void logAnalyticsEvent('streak_celebration_closed', {
          current_streak: length,
          celebration_variant: variant,
        });
      }
      if (variant === 'goal') claimGoal();
      setDismissedAt(length);

      const needsAGoal = goal === null || isStreakGoalReached(goal, length ?? 0);
      const detours =
        variant === 'start' || (variant === 'milestone' && needsAGoal);
      if (detours)
        navigate(ROUTES.CHILD_STREAK_GOAL.path, { state: detourState });
      return detours;
    },
  };
};

export default useStreakCelebration;
